/**
 * Hook for fetching order assignment count data from Postgres.
 * @module useOrderAssignmentData
 */

import moment from "moment";
import { useEffect } from "react";

import { getFacetsCountFromPostgres } from "../../../../api/postgreSQL/postCalls/getFacetsCountFromPostgres";
import { PostgresFacetCountResponseModel } from "../../../../data-model/postgresFacetCountResponseModel";
import { useAbortableFetch } from "../../../../hooks/useAbortableFetch";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";

/**
 * Fetches today's order assignment counts grouped by assignee name.
 *
 * Wraps `getFacetsCountFromPostgres` with `useAbortableFetch` so that
 * in-flight requests are automatically cancelled on unmount or re-fetch.
 *
 * @returns data  - The facet count response (null until loaded).
 * @returns loading - Whether the fetch is in progress.
 * @returns error   - Error object if the fetch failed.
 */
export const useOrderAssignmentData = () => {
  const { runFetch, abort, data, loading, error } =
    useAbortableFetch<PostgresFacetCountResponseModel>(async (signal) => {
      return getFacetsCountFromPostgres(
        {
          org_id: getOrgIdForFetchExternalWorklist(),
          facet_attributes: ["assigned_to_name"],
          created_at_start: moment().format("YYYY-MM-DD"),
          created_at_end: moment().format("YYYY-MM-DD"),
        },
        signal,
      );
    });

  useEffect(() => {
    runFetch();
    return () => abort();
  }, []);

  return { data, loading, error };
};
